import type { ShippingAction, ShippingCondition, ShippingRule } from "@logiful/domain";
import { ArrowDown, ArrowUp, EyeOff, Plus, Trash2, X } from "lucide-react";
import type { ReactNode } from "react";
import { Badge, Button, Card, cx, Input } from "./ui";

import { t, msg } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
export interface Option { value: string; label: string }

/** Felder für Bedingungen – "staffOnly" sind nur für Logiful (Verpackung) */
const FIELDS: Record<ShippingCondition["field"], { label: string; ops: { op: string; label: string }[]; value: "countries" | "number" | "text" | "list" | "packagings" | "none" | "dims" | "name"; unit?: string; staffOnly?: boolean }> = {
  country: { label: msg("Zielland"), ops: [{ op: "in", label: msg("ist eines von") }, { op: "not_in", label: msg("ist keines von") }], value: "countries" },
  weightKg: { label: msg("Gesamtgewicht"), ops: cmpOps(), value: "number", unit: "kg" },
  longestSideCm: { label: msg("Längste Paketseite"), ops: cmpOps(), value: "number", unit: "cm" },
  packages: { label: msg("Anzahl Pakete"), ops: cmpOps(), value: "number" },
  orderValue: { label: msg("Warenwert"), ops: cmpOps(), value: "number", unit: "€" },
  tag: { label: msg("Auftrags-Tag"), ops: [{ op: "has", label: msg("vorhanden") }, { op: "has_not", label: msg("nicht vorhanden") }], value: "text" },
  sku: { label: msg("Artikel (SKU)"), ops: [{ op: "contains_any", label: msg("enthält einen von") }, { op: "contains_none", label: msg("enthält keinen von") }], value: "list" },
  dangerousGoods: { label: msg("Gefahrgut im Auftrag"), ops: [{ op: "is", label: msg("ja") }, { op: "is_not", label: msg("nein") }], value: "none" },
  fragile: { label: msg("Zerbrechliches im Auftrag"), ops: [{ op: "is", label: msg("ja") }, { op: "is_not", label: msg("nein") }], value: "none" },
  business: { label: msg("Geschäftskunde (Firma)"), ops: [{ op: "is", label: msg("ja") }, { op: "is_not", label: msg("nein") }], value: "none" },
  shopShippingMethod: { label: msg("Versandart im Shop"), ops: [{ op: "contains", label: msg("enthält") }, { op: "equals", label: msg("ist genau") }], value: "text" },
  packageFits: { label: msg("Jedes Paket passt in"), ops: [{ op: "fits", label: msg("passt in") }, { op: "not_fits", label: msg("passt nicht in") }], value: "dims" },
  maxPackageWeightKg: { label: msg("Schwerstes Paket (inkl. Verpackung)"), ops: cmpOps(), value: "number", unit: "kg" },
  methodAvailable: { label: msg("Freigegebene Versandart"), ops: [{ op: "exists", label: msg("vorhanden, Name enthält") }, { op: "not_exists", label: msg("nicht vorhanden, Name enthält") }], value: "name" },
  packaging: { label: msg("Berechnete Verpackung"), ops: [{ op: "in", label: msg("ist eine von") }, { op: "not_in", label: msg("ist keine von") }], value: "packagings", staffOnly: true },
};
function cmpOps() {
  return [{ op: "gt", label: msg("größer als") }, { op: "gte", label: msg("mindestens") }, { op: "lt", label: msg("kleiner als") }, { op: "lte", label: msg("höchstens") }];
}

const ACTIONS: Record<ShippingAction["type"], { label: string; hint: string; staffOnly?: boolean }> = {
  force: { label: msg("Versandart vorgeben"), hint: msg("genau diese, wenn sie für die Sendung zulässig ist") },
  allowOnly: { label: msg("Nur diese Versandarten"), hint: msg("Auswahl einschränken") },
  exclude: { label: msg("Versandarten ausschließen"), hint: "" },
  prefer: { label: msg("Versandart bevorzugen"), hint: msg("sofern zulässig, vor allen anderen") },
  requireService: { label: msg("Mindest-Service"), hint: msg("z. B. Express bleibt Express") },
  optimize: { label: msg("Günstigste zulässige wählen"), hint: msg("nach Logiful-Einkaufspreisen"), staffOnly: true },
  usePackaging: { label: msg("Verpackung vorgeben"), hint: "", staffOnly: true },
  forceByName: { label: msg("Versandart vorgeben – Name enthält"), hint: msg("unter den freigegebenen; bei mehreren die günstigste") },
  preferByName: { label: msg("Bevorzugen – Name enthält"), hint: "" },
  excludeByName: { label: msg("Ausschließen – Name enthält"), hint: "" },
  hold: { label: msg("Zur Prüfung zurückhalten"), hint: msg("mit Grund") },
};

const SERVICES: Option[] = [{ value: "economy", label: msg("Economy") }, { value: "standard", label: msg("Standard") }, { value: "express", label: msg("Express") }];

const selectCls = "h-8 rounded-lg border border-border bg-surface px-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-fg/10";

function defaultCondition(field: ShippingCondition["field"]): ShippingCondition {
  const f = FIELDS[field];
  const op = f.ops[0]!.op;
  if (field === "packageFits") return { field, op: "fits", l: 35, w: 25, h: 8, toleranceCm: 0 };
  if (field === "methodAvailable") return { field, op: "exists", nameContains: "" };
  const value = f.value === "number" ? 0 : f.value === "text" ? "" : f.value === "none" ? undefined : [];
  return (value === undefined ? { field, op } : { field, op, value }) as ShippingCondition;
}
function defaultAction(type: ShippingAction["type"], methods: Option[]): ShippingAction {
  switch (type) {
    case "force": case "prefer": return { type, methodId: methods[0]?.value ?? "" };
    case "allowOnly": case "exclude": return { type, methodIds: [] };
    case "requireService": return { type, service: "express" };
    case "optimize": return { type };
    case "usePackaging": return { type, packagingIds: [] };
    case "hold": return { type, reason: "" };
    case "forceByName": case "preferByName": case "excludeByName": return { type, nameContains: "" };
  }
}

/** Mehrfachauswahl als Chips */
function Multi({ value, options, onChange, placeholder }: { value: string[]; options: Option[]; onChange: (v: string[]) => void; placeholder: string }) {
  const rest = options.filter((o) => !value.includes(o.value));
  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-md bg-surface-2 border border-border text-[12px]">
          {options.find((o) => o.value === v)?.label ?? v}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="size-4 grid place-items-center text-fg-subtle hover:text-fg" aria-label={t("entfernen")}><X className="size-3" /></button>
        </span>
      ))}
      {rest.length > 0 && (
        <select value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} className={cx(selectCls, "text-fg-muted")}>
          <option value="">{value.length ? t("+ hinzufügen") : placeholder}</option>
          {rest.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
        </select>
      )}
    </div>
  );
}

/** Freie Liste (Länder, SKUs) – Komma/Leerzeichen getrennt */
function ListInput({ value, onChange, placeholder, upper }: { value: string[]; onChange: (v: string[]) => void; placeholder: string; upper?: boolean }) {
  return (
    <Input
      defaultValue={value.join(", ")}
      onBlur={(e) => onChange(e.target.value.split(/[\s,;]+/).map((x) => (upper ? x.trim().toUpperCase() : x.trim())).filter(Boolean))}
      placeholder={placeholder}
      className="font-mono w-56"
    />
  );
}

function ConditionRow({ c, onChange, onRemove, packagings }: { c: ShippingCondition; onChange: (c: ShippingCondition) => void; onRemove: () => void; packagings: Option[] }) {
  const f = FIELDS[c.field];
  const val = (c as { value?: unknown }).value;
  const setVal = (v: unknown) => onChange({ ...c, value: v } as ShippingCondition);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[13px] font-medium min-w-[160px]">{t(f.label)}</span>
      <select value={c.op} onChange={(e) => onChange({ ...c, op: e.target.value } as ShippingCondition)} className={selectCls}>
        {f.ops.map((o) => <option key={o.op} value={o.op}>{t(o.label)}</option>)}
      </select>
      {f.value === "number" && (
        <span className="inline-flex items-center gap-1">
          <Input type="number" step="0.01" min={0} value={String(val ?? 0)} onChange={(e) => setVal(Number(e.target.value))} className="w-24 tabular-nums" />
          {f.unit && <span className="text-[12px] text-fg-muted">{f.unit}</span>}
        </span>
      )}
      {f.value === "text" && <Input value={String(val ?? "")} onChange={(e) => setVal(e.target.value)} className="w-48" placeholder={c.field === "tag" ? t("z. B. vip") : t("z. B. Express")} />}
      {f.value === "countries" && <ListInput value={(val as string[]) ?? []} onChange={setVal} placeholder={t("DE, AT, CH")} upper />}
      {f.value === "list" && <ListInput value={(val as string[]) ?? []} onChange={setVal} placeholder={t("SKU-1, SKU-2")} />}
      {c.field === "packageFits" && (
        <span className="inline-flex flex-wrap items-center gap-1 text-[12px] text-fg-muted">
          {(["l", "w", "h"] as const).map((k, ki) => (
            <span key={k} className="inline-flex items-center gap-1">
              {ki > 0 && "×"}
              <Input type="number" step="0.5" min={0} value={String(c[k])} onChange={(e) => onChange({ ...c, [k]: Number(e.target.value) })} className="w-16 tabular-nums" aria-label={k} />
            </span>
          ))}
          <span>{t("cm Außenmaß, Sicherheitszuschlag")}</span>
          <Input type="number" step="0.5" min={0} max={10} value={String(c.toleranceCm)} onChange={(e) => onChange({ ...c, toleranceCm: Number(e.target.value) })} className="w-16 tabular-nums" aria-label={t("Toleranz")} />
          <span>{t("cm je Seite")}</span>
        </span>
      )}
      {c.field === "methodAvailable" && <Input value={c.nameContains} onChange={(e) => onChange({ ...c, nameContains: e.target.value })} className="w-48" placeholder={t("z. B. Kleinpaket")} />}
      {f.value === "packagings" && <Multi value={(val as string[]) ?? []} options={packagings} onChange={setVal} placeholder={t("Verpackung wählen")} />}
      <button type="button" onClick={onRemove} className="ml-auto size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={t("Bedingung entfernen")}><Trash2 className="size-3.5" /></button>
    </div>
  );
}

function ActionRow({ a, onChange, onRemove, methods, packagings }: { a: ShippingAction; onChange: (a: ShippingAction) => void; onRemove: () => void; methods: Option[]; packagings: Option[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[13px] font-medium min-w-[160px]">{t(ACTIONS[a.type].label)}</span>
      {(a.type === "force" || a.type === "prefer") && (
        <select value={a.methodId} onChange={(e) => onChange({ ...a, methodId: e.target.value })} className={selectCls}>
          {!methods.some((m) => m.value === a.methodId) && <option value={a.methodId}>{a.methodId || t("Versandart wählen")}</option>}
          {methods.map((m) => <option key={m.value} value={m.value}>{t(m.label)}</option>)}
        </select>
      )}
      {(a.type === "allowOnly" || a.type === "exclude") && <Multi value={a.methodIds} options={methods} onChange={(v) => onChange({ ...a, methodIds: v })} placeholder={t("Versandarten wählen")} />}
      {a.type === "requireService" && (
        <select value={a.service} onChange={(e) => onChange({ ...a, service: e.target.value as "express" })} className={selectCls}>
          {SERVICES.map((s) => <option key={s.value} value={s.value}>{t(s.label)}</option>)}
        </select>
      )}
      {a.type === "usePackaging" && <Multi value={a.packagingIds} options={packagings} onChange={(v) => onChange({ ...a, packagingIds: v })} placeholder={t("Verpackungen wählen")} />}
      {(a.type === "forceByName" || a.type === "preferByName" || a.type === "excludeByName") && (
        <>
          <Input value={a.nameContains} onChange={(e) => onChange({ ...a, nameContains: e.target.value })} placeholder={t("z. B. Kleinpaket")} className="w-48" />
          {a.nameContains && <span className="text-[12px] text-fg-muted">→ {methods.filter((m) => `${m.label} ${m.value}`.toLowerCase().includes(a.nameContains.toLowerCase())).map((m) => m.label).join(", ") || t("derzeit keine passende")}</span>}
        </>
      )}
      {a.type === "hold" && <Input value={serverText(a.reason)} onChange={(e) => onChange({ ...a, reason: e.target.value })} placeholder={t("Grund, z. B. Versicherung prüfen")} className="w-72" />}
      {ACTIONS[a.type].hint && <span className="text-[12px] text-fg-subtle">{t(ACTIONS[a.type].hint)}</span>}
      <button type="button" onClick={onRemove} className="ml-auto size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={t("Aktion entfernen")}><Trash2 className="size-3.5" /></button>
    </div>
  );
}

function AddMenu<K extends string>({ options, onPick, label }: { options: [K, string][]; onPick: (k: K) => void; label: string }) {
  return (
    <select value="" onChange={(e) => e.target.value && onPick(e.target.value as K)} className={cx(selectCls, "text-fg-muted border-dashed")}>
      <option value="">{t(label)}</option>
      {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-subtle mb-1.5">{t(title)}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/**
 * Editor für Versandregeln. staff = Logiful: darf interne (versteckte) Regeln, Optimierung und Verpackung nutzen.
 * lockVisibility: alle Regeln fest intern (globale Logiful-Regeln).
 */
export function ShippingRuleEditor({ rules, onChange, methods, packagings, staff, lockVisibility }: { rules: ShippingRule[]; onChange: (r: ShippingRule[]) => void; methods: Option[]; packagings: Option[]; staff: boolean; lockVisibility?: "internal" }) {
  const set = (i: number, r: ShippingRule) => onChange(rules.map((x, j) => (j === i ? r : x)));
  const move = (i: number, d: -1 | 1) => {
    const next = [...rules];
    const [r] = next.splice(i, 1);
    next.splice(i + d, 0, r!);
    onChange(next);
  };
  const visibility = lockVisibility ?? (staff ? "internal" : "customer");
  const add = () => onChange([...rules, { id: `r${Date.now()}`, name: "Neue Regel", enabled: true, visibility, when: [], unless: [], then: [defaultAction("force", methods)] }]);
  const addKleinpaket = () => onChange([...rules, {
    id: `r${Date.now()}`, name: "Kleinpaket, wo es passt", enabled: true, visibility,
    when: [
      { field: "methodAvailable", op: "exists", nameContains: "kleinpaket" },
      { field: "packageFits", op: "fits", l: 35, w: 25, h: 8, toleranceCm: 0 },
      { field: "maxPackageWeightKg", op: "lt", value: 1 },
    ],
    unless: [{ field: "country", op: "not_in", value: ["DE"] }],
    then: [{ type: "forceByName", nameContains: "kleinpaket" }],
  }]);
  const fieldOptions = (Object.keys(FIELDS) as ShippingCondition["field"][]).filter((k) => staff || !FIELDS[k].staffOnly).map((k) => [k, FIELDS[k].label] as [ShippingCondition["field"], string]);
  const actionOptions = (Object.keys(ACTIONS) as ShippingAction["type"][]).filter((k) => staff || !ACTIONS[k].staffOnly).map((k) => [k, ACTIONS[k].label] as [ShippingAction["type"], string]);

  return (
    <div className="space-y-3">
      {rules.length === 0 && <Card className="p-6 text-center text-[13px] text-fg-muted">{t("Noch keine Regeln. Ohne Regel bleibt die im Shop oder Auftrag gewählte Versandart.")}</Card>}
      {rules.map((r, i) => {
        const internal = r.visibility === "internal";
        return (
          <Card key={r.id} className={cx("overflow-hidden", !r.enabled && "opacity-60", internal && "border-ink/30")}>
            <div className={cx("px-4 py-2.5 flex flex-wrap items-center gap-2 border-b border-border", internal ? "bg-ink/[0.04]" : "bg-surface-2/40")}>
              <span className="text-[12px] tabular-nums text-fg-subtle w-5">{i + 1}.</span>
              <Input value={r.name} onChange={(e) => set(i, { ...r, name: e.target.value })} className="flex-1 min-w-[200px] font-medium" aria-label={t("Name der Regel")} />
              {staff && !lockVisibility && (
                <div className="inline-flex p-0.5 rounded-lg bg-surface border border-border">
                  {([["customer", msg("Für Kunden sichtbar")], ["internal", msg("Logiful intern")]] as const).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => set(i, { ...r, visibility: v })} className={cx("h-7 px-2.5 rounded-md text-[12px] font-medium inline-flex items-center gap-1", r.visibility === v ? (v === "internal" ? "bg-ink text-ink-fg" : "bg-surface-2") : "text-fg-muted hover:text-fg")}>
                      {v === "internal" && <EyeOff className="size-3" />} {l}
                    </button>
                  ))}
                </div>
              )}
              {internal && (lockVisibility || !staff) && <Badge icon={<EyeOff className="size-3" />}>{t("intern")}</Badge>}
              <button type="button" role="switch" aria-checked={r.enabled} onClick={() => set(i, { ...r, enabled: !r.enabled })} className="inline-flex items-center gap-1.5 text-[12px] text-fg-muted">
                <span className={cx("w-7 h-4 rounded-full p-0.5 transition-colors", r.enabled ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3 rounded-full bg-surface transition-transform", r.enabled && "translate-x-3")} /></span>
                {t("aktiv")}
              </button>
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 disabled:opacity-30" aria-label={t("nach oben")}><ArrowUp className="size-3.5" /></button>
              <button type="button" disabled={i === rules.length - 1} onClick={() => move(i, 1)} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 disabled:opacity-30" aria-label={t("nach unten")}><ArrowDown className="size-3.5" /></button>
              <button type="button" onClick={() => onChange(rules.filter((_, j) => j !== i))} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={t("Regel löschen")}><Trash2 className="size-3.5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <Section title={r.when.length ? t("Wenn (alle Bedingungen)") : t("Wenn – immer (keine Bedingung)")}>
                {r.when.map((c, ci) => (
                  <ConditionRow key={ci} c={c} packagings={packagings} onChange={(nc) => set(i, { ...r, when: r.when.map((x, j) => (j === ci ? nc : x)) })} onRemove={() => set(i, { ...r, when: r.when.filter((_, j) => j !== ci) })} />
                ))}
                <AddMenu label={t("+ Bedingung")} options={fieldOptions} onPick={(f) => set(i, { ...r, when: [...r.when, defaultCondition(f)] })} />
              </Section>
              <Section title={(r.unless ?? []).length ? t("Außer wenn (eine genügt – Regel greift dann nicht)") : t("Außer wenn – keine Ausnahme")}>
                {(r.unless ?? []).map((c, ci) => (
                  <ConditionRow key={ci} c={c} packagings={packagings} onChange={(nc) => set(i, { ...r, unless: (r.unless ?? []).map((x, j) => (j === ci ? nc : x)) })} onRemove={() => set(i, { ...r, unless: (r.unless ?? []).filter((_, j) => j !== ci) })} />
                ))}
                <AddMenu label={t("+ Ausnahme")} options={fieldOptions} onPick={(f) => set(i, { ...r, unless: [...(r.unless ?? []), defaultCondition(f)] })} />
              </Section>
              <Section title={t("Dann")}>
                {r.then.map((a, ai) => (
                  <ActionRow key={ai} a={a} methods={methods} packagings={packagings} onChange={(na) => set(i, { ...r, then: r.then.map((x, j) => (j === ai ? na : x)) })} onRemove={() => set(i, { ...r, then: r.then.filter((_, j) => j !== ai) })} />
                ))}
                <AddMenu label={t("+ Aktion")} options={actionOptions} onPick={(t) => set(i, { ...r, then: [...r.then, defaultAction(t, methods)] })} />
              </Section>
            </div>
          </Card>
        );
      })}
      <div className="flex flex-wrap gap-2">
        <Button icon={<Plus className="size-4" />} onClick={add}>{t("Regel hinzufügen")}</Button>
        <Button variant="ghost" onClick={addKleinpaket}>{t("Vorlage: Kleinpaket bis 35 × 25 × 8 cm, < 1 kg")}</Button>
      </div>
      <p className="text-[12px] text-fg-subtle">{t("Reihenfolge zählt: Kundenregeln zuerst, dann Logiful-interne Regeln (sie können Kundenregeln übersteuern). Ausnahmen („außer wenn“) übersteuern Maß- und Gewichtsbedingungen. Maße und Gewichte gelten je Paket inklusive Verpackung. Eine Versandart wird nur gewählt, wenn sie zur Sendung passt (Gewicht, Maße, Land, Gefahrgut).")}</p>
    </div>
  );
}
